// v1.0.0 - 2026-01-28 - Admin configurations list page

import { useCallback } from 'react'
import { Link } from 'react-router-dom'
import { useAuthStore } from '@/lib/stores/authStore'
import { useQuery } from '@/lib/hooks'
import { cer } from '@/lib/api/client'
import { PageContainer, PageHeader } from '@/components/layout'
import { Card, CardContent, CardHeader, CardTitle, Badge, Spinner } from '@/components/ui'

interface Configuration {
  id: string
  name: string
  crin: string
  zona: 'NORD' | 'CENTRO' | 'SUD'
  status: 'active' | 'inactive' | 'pending'
  memberCount: number
}

const statusLabels: Record<string, string> = {
  active: 'Attivo',
  inactive: 'Inattivo',
  pending: 'In attesa',
}

const statusColors: Record<string, 'success' | 'error' | 'warning'> = {
  active: 'success',
  inactive: 'error',
  pending: 'warning',
}

const zonaLabels: Record<string, string> = {
  NORD: 'Nord',
  CENTRO: 'Centro',
  SUD: 'Sud',
}

export function ConfigurationsList() {
  const { user } = useAuthStore()

  const fetchConfigurations = useCallback(
    () => (user?.cer_id ? cer.getConfigurations(user.cer_id) : Promise.resolve(null)),
    [user?.cer_id]
  )

  const { data, isLoading } = useQuery(fetchConfigurations, {
    enabled: !!user?.cer_id,
  })

  const configurations = (data?.configurations || []) as Configuration[]

  return (
    <PageContainer>
      <PageHeader
        title="Configurazioni CRIN"
        description="Gestisci le configurazioni della Comunita Energetica"
      />

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : configurations.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center text-muted-foreground">
              <svg
                className="mx-auto h-12 w-12 mb-4 opacity-50"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                />
              </svg>
              <p className="text-lg font-medium">Nessuna configurazione</p>
              <p className="mt-1">Non sono presenti configurazioni CRIN per questa CER.</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Lista Configurazioni</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="rounded-md border-0">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="h-12 px-6 text-left align-middle font-medium">CRIN</th>
                      <th className="h-12 px-6 text-left align-middle font-medium">Zona</th>
                      <th className="h-12 px-6 text-left align-middle font-medium">Stato</th>
                      <th className="h-12 px-6 text-left align-middle font-medium">Membri</th>
                      <th className="h-12 px-6 text-left align-middle font-medium">Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {configurations.map((config) => (
                      <tr key={config.id} className="border-b hover:bg-muted/50 transition-colors">
                        <td className="p-6">
                          <div className="font-medium font-mono">{config.crin}</div>
                        </td>
                        <td className="p-6">
                          <Badge variant="default">{zonaLabels[config.zona] || config.zona}</Badge>
                        </td>
                        <td className="p-6">
                          <Badge variant={statusColors[config.status]}>
                            {statusLabels[config.status]}
                          </Badge>
                        </td>
                        <td className="p-6">
                          <span className="text-muted-foreground">{config.memberCount}</span>
                        </td>
                        <td className="p-6">
                          <Link
                            to={`/admin/configurations/${config.id}`}
                            className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
                          >
                            Dettagli
                            <svg
                              className="h-4 w-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 5l7 7-7 7"
                              />
                            </svg>
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="py-4">
              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <span>
                  Totale: {configurations.length} configurazion{configurations.length === 1 ? 'e' : 'i'}
                </span>
                <span>
                  {configurations.filter((c) => c.status === 'active').length} attiv{configurations.filter((c) => c.status === 'active').length === 1 ? 'a' : 'e'}
                </span>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </PageContainer>
  )
}
